import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import { Button } from "@/components/ui/button";
import { MapPin, Loader2 } from "lucide-react";
import { geocodeAddress } from "@/lib/geocoding";
import { FINLAND_CENTER } from "@/config/locations";
import "@/lib/leaflet-setup";

interface CemeteryLocationPickerProps {
  latitude: string;
  longitude: string;
  address: string;
  postalCode: string;
  city: string;
  onLocationChange: (lat: string, lng: string) => void;
}

export function CemeteryLocationPicker({
  latitude,
  longitude,
  address,
  postalCode,
  city,
  onLocationChange,
}: CemeteryLocationPickerProps) {
  const [isGeocoding, setIsGeocoding] = useState(false);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);

  const lat = latitude ? parseFloat(latitude) : null;
  const lng = longitude ? parseFloat(longitude) : null;
  const hasCoords = lat !== null && lng !== null && !isNaN(lat) && !isNaN(lng);

  // Initialisoi kartta kerran
  useEffect(() => {
    if (!mapContainerRef.current || mapInstanceRef.current) return;

    const initialCenter: L.LatLngExpression = hasCoords
      ? [lat!, lng!]
      : FINLAND_CENTER;
    const initialZoom = hasCoords ? 13 : 6;

    const map = L.map(mapContainerRef.current).setView(initialCenter, initialZoom);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    map.on("click", (e: L.LeafletMouseEvent) => {
      onLocationChange(e.latlng.lat.toFixed(6), e.latlng.lng.toFixed(6));
    });

    mapInstanceRef.current = map;

    return () => {
      map.remove();
      mapInstanceRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Päivitä markeri ja kartan sijainti kun koordinaatit muuttuvat
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    if (hasCoords) {
      const latlng: L.LatLngExpression = [lat!, lng!];
      map.setView(latlng, Math.max(map.getZoom(), 13));
      if (markerRef.current) {
        markerRef.current.setLatLng(latlng);
      } else {
        markerRef.current = L.marker(latlng).addTo(map);
      }
    } else {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
    }
  }, [lat, lng, hasCoords]);

  const handleGeocode = async () => {
    if (!address || !postalCode || !city) return;
    setIsGeocoding(true);
    try {
      const result = await geocodeAddress(address, postalCode, city);
      if (result) {
        onLocationChange(result.lat.toFixed(6), result.lng.toFixed(6));
      }
    } finally {
      setIsGeocoding(false);
    }
  };

  const canGeocode = Boolean(address && postalCode && city);

  return (
    <div className="space-y-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleGeocode}
        disabled={!canGeocode || isGeocoding}
        className="w-full"
      >
        {isGeocoding ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <MapPin className="mr-2 h-4 w-4" />
        )}
        {isGeocoding ? "Haetaan..." : "Hae koordinaatit osoitteesta"}
      </Button>

      <div className="rounded-md overflow-hidden border h-52">
        <div ref={mapContainerRef} style={{ height: "100%", width: "100%" }} />
      </div>
      <p className="text-xs text-muted-foreground">
        Klikkaa karttaa asettaaksesi sijainnin
      </p>
    </div>
  );
}
